import { Badge, Icon } from "@elaniintc/goes-design-system/ui";
import type { NotificationDetail } from "./types";

interface DetailLogProps {
  detail: NotificationDetail;
}

const containerClassName =
  "bg-neutral-object-accent-silent border border-neutral-quiet rounded-lg p-2 w-full min-h-11";

/**
 * DetailLog component displays the visual representation of the change
 * according to the notification detail type.
 *
 * Types:
 * - status-change: De [Observado] a [Aprobado]
 * - status-assignment: Marcado como [Aprobado]
 * - comment: Comentario con texto
 * - content-change: Descripción del cambio de contenido
 * - attachment: Lista de archivos adjuntos
 * - content-replacement: [archivo v1.pdf] → [archivo v2.pdf]
 * - system-update: System update notification with version and message
 * - signature-error: Document signature failed with error details
 */
export const DetailLog = ({ detail }: DetailLogProps) => {
  // Status Change: De [status A] a [status B]
  if (detail.type === "status-change") {
    return (
      <div
        className={`flex items-center gap-2 flex-wrap ${containerClassName}`}
      >
        <span className="text-body text-size-body-md">De</span>
        <Badge variant="warning" size="sm">
          {detail.fromStatus}
          <Icon type="fas" name="file-pen" className="text-xs" />
        </Badge>
        <span className="text-body text-size-body-md">a</span>
        <Badge variant="success" size="sm">
          {detail.toStatus}
          <Icon type="fas" name="check" className="text-xs" />
        </Badge>
      </div>
    );
  }

  // Status Assignment: Marcado como [status]
  if (detail.type === "status-assignment" && detail.assignedStatus) {
    return (
      <div
        className={`flex items-center gap-2 flex-wrap ${containerClassName}`}
      >
        <span className="text-body text-size-body-md">Marcado como</span>
        <Badge variant="success" size="sm">
          {detail.assignedStatus}
          <Icon type="fas" name="check" className="text-xs" />
        </Badge>
      </div>
    );
  }

  // Content Change: Descripción del cambio
  if (detail.type === "content-change" && detail.changedContent) {
    return (
      <div className={`flex flex-col gap-1 ${containerClassName}`}>
        <div className="flex items-center gap-2">
          <Icon
            type="fas"
            name="pen-to-square"
            className="text-action-quiet text-sm"
          />
          <span className="text-action font-normal text-size-body-md">
            Cambio de contenido:
          </span>
        </div>
        <p className="text-body text-size-body-sm font-normal">
          {detail.changedContent}
        </p>
      </div>
    );
  }

  // Attachment: Lista de archivos
  if (detail.type === "attachment" && detail.files) {
    return (
      <div
        className={`flex items-center gap-2 flex-wrap ${containerClassName}`}
      >
        {detail.files.map((file, index) => (
          <Badge key={index} variant="neutral" size="sm">
            {file}
            <Icon type="fas" name="file" className="text-xs" />
          </Badge>
        ))}
      </div>
    );
  }

  // Content Replacement: [oldValue] → [newValue]
  if (
    detail.type === "content-replacement" &&
    detail.oldValue &&
    detail.newValue
  ) {
    return (
      <div
        className={`flex items-center gap-2 flex-wrap ${containerClassName}`}
      >
        <Badge variant="neutral" size="sm">
          {detail.oldValue}
          <Icon type="fas" name="file" className="text-xs" />
        </Badge>
        <Icon
          type="fas"
          name="arrow-right"
          className="text-body-quiet text-sm"
        />
        <Badge variant="neutral" size="sm">
          {detail.newValue}
          <Icon type="fas" name="file" className="text-xs" />
        </Badge>
      </div>
    );
  }

  // Signature Error: Document signature failed
  if (detail.type === "signature-error") {
    const fileName = detail.metadata?.file_name || "Documento.pdf";

    return (
      <div
        className={`flex items-center justify-between gap-2 ${containerClassName}`}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="text-body text-size-body-sm font-normal flex items-center min-w-0 max-w-full truncate">
            {fileName}
          </span>
        </div>
        <Badge variant="danger" size="sm" className="shrink-0">
          {detail.errorMessage}
          <Icon type="fas" name="xmark" className="text-xs" />
        </Badge>
      </div>
    );
  }

  // Status assignment with correction fields
  if (
    detail.type === "status-assignment" &&
    (detail.correction_fields_denied || detail.correction_fields_approved)
  ) {
    return (
      <div
        className={`flex items-center text-body gap-2 ${containerClassName}`}
      >
        {detail.correction_fields_denied ? (
          <>
            <span className="truncate">
              {detail.correction_fields_denied} campos
            </span>
            <Badge variant="warning" size="xs" className="shrink-0">
              Observados
              <Icon
                type="fas"
                name="file-edit"
                className="text-xs text-warning-quiet"
              />
            </Badge>
          </>
        ) : null}
        {detail.correction_fields_approved ? (
          <>
            <span className="truncate">
              {detail.correction_fields_approved} campos
            </span>
            <Badge variant="success" size="xs" className="shrink-0">
              Aprobados
              <Icon
                type="fas"
                name="check"
                className="text-xs text-success-quiet"
              />
            </Badge>
          </>
        ) : null}
      </div>
    );
  }

  // Correction Rejection: Rechazo de corrección sin campos específicos
  if (detail.type === "correction_rejection") {
    return (
      <div
        className={`flex items-center text-body gap-2 ${containerClassName}`}
      >
        <span className="truncate">De</span>
        <Badge variant="warning" size="xs" className="shrink-0">
          En revisión de líder
          <Icon
            type="fas"
            name="file-edit"
            className="text-xs text-warning-quiet"
          />
        </Badge>
        <span className="truncate">a</span>
        <Badge variant="info" size="xs" className="shrink-0">
          Pendiente
          <Icon type="fas" name="clock" className="text-xs text-info-quiet" />
        </Badge>
      </div>
    );
  }

  // Extension Request: Solicitud de Prórroga
  // action: undefined | "scaled" → solo badge de Prórroga (estilo creación)
  // action: "approved"           → + badge verde Aprobada
  // action: "rejected"           → + badge rojo Rechazada
  if (detail.type === "extension_request") {
    const action = detail.metadata?.action as string | undefined;

    return (
      <div
        className={`flex items-center gap-2 flex-wrap ${containerClassName}`}
      >
        <span className="text-body text-size-body-md">Solicitud de</span>
        <Badge variant="warning" size="sm">
          Prórroga
          <Icon type="fas" name="timer" className="text-xs" />
        </Badge>
        {action === "approved" && (
          <span className="text-body text-size-body-md">ha sido aprobada</span>
        )}
        {(action === "rejected" || action === "denied") && (
          <Badge variant="danger" size="sm">
            Rechazada
            <Icon type="fas" name="xmark" className="text-xs" />
          </Badge>
        )}
      </div>
    );
  }

  // Suspension Request: Solicitud de Suspensión de plazo
  // action: undefined | "scaled" → solo badge de Suspensión (estilo creación)
  // action: "approved"           → + badge verde Aprobada
  // action: "rejected"           → + badge rojo Rechazada
  if (detail.type === "suspension_request") {
    const action = detail.metadata?.action as string | undefined;

    return (
      <div
        className={`flex items-center gap-2 flex-wrap ${containerClassName}`}
      >
        <span className="text-body text-size-body-md">Solicitud de</span>
        <Badge variant="warning" size="sm">
          Suspensión
          <Icon type="fas" name="circle-stop" className="text-xs" />
        </Badge>
        {action === "approved" && (
          <Badge variant="success" size="sm">
            Aprobada
            <Icon type="fas" name="check" className="text-xs" />
          </Badge>
        )}
        {(action === "rejected" || action === "denied") && (
          <Badge variant="danger" size="sm">
            Denegada
            <Icon type="fas" name="xmark" className="text-xs" />
          </Badge>
        )}
      </div>
    );
  }

  return null;
};
